import { setSession } from "../utils/session-helper";

const Admin = (app) => {
  app.post(
    "/admin-panel/manage-category",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;

      const { categoryId, category, newCategory = false } = body;

      assert(category, 400, "You need to provide a category name");

      if (!newCategory) {
        assert(categoryId, 400, "You need to provide a category id");
      }

      const method = newCategory ? "POST" : "PUT";

      await api(`tutorial-categories`, {
        method,
        body,
      });

      flash(
        "success",
        `Category ${newCategory ? "created" : "changes saved"} succesfully`
      );

      res.refreshWith(
        {
          "modal[type]": "",
          category,
          selectedCategoryIndex: "",
          selectedCategoryId: "",
          newCategory: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/admin-panel/manage-category/sort",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;

      const { sortedCategories } = body;

      assert(sortedCategories, 400, "You need to provide a category name");

      try {
        await api(`tutorial-categories-sort`, {
          method: "POST",
          body,
        });

        flash("success", `Sorted successfully`);
      } catch (err) {
        flash("error", `Error sorting categories`);
      }

      res.refreshWith(
        {
          "modal[type]": "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/admin-panel/manage-category/delete",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;

      const { category, product } = body;

      assert(category !== "All", 400, "You can't delete the All category");

      assert(category, 400, "You need to provide a category name");
      assert(product, 400, "You need to provide a product name");

      try {
        await api(
          `tutorial-categories?category=${category}&product=${product}`,
          {
            method: "DELETE",
          }
        );

        flash(
          "success",
          `Category ${category} for ${product} removed successfully`
        );
      } catch (err) {
        flash("error", `Error removing category ${category} for ${product}`);
      }

      res.refreshWith(
        {
          "modal[type]": "",
          category: "All",
          selectedCategoryIndex: "all",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/admin-panel/manage-category/video",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;

      const { category, name, url, description, product } = body;

      assert(name, 400, "You need to provide a video title");
      assert(url, 400, "You need to provide a video url");
      assert(description, 400, "You need to provide a video description");
      assert(category, 400, "You need to provide a category");
      assert(product, 400, "You need to provide a product");

      const loomRegexp = /(https:\/\/)www.loom.com\/share\/(.+)/gim;
      const match = url.match(loomRegexp);
      assert(match, 400, "You need to provide a valid loom url");

      try {
        await api("tutorial-categories-videos", {
          method: "POST",
          body,
        });

        flash("success", `Video added succesfully`);
      } catch (err) {
        flash("error", "Error adding video");
      }

      res.refreshWith(
        {
          "modal[type]": "",
          selectedTutorial: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/admin-panel/manage-category/video/update",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;
      const { video, url } = body;
      assert(video, 400, "You need to provide a video");

      if (url) {
        const loomRegexp = /(https:\/\/)www.loom.com\/share\/(.+)/gim;
        const match = url.match(loomRegexp);
        assert(match, 400, "You need to provide a valid loom url");
      }

      try {
        await api("tutorial-categories-videos", {
          method: "PUT",
          body,
        });

        flash("success", `Video modified succesfully`);
      } catch (err) {
        flash("error", "Error modifying video");
      }

      res.refreshWith(
        {
          "modal[type]": "",
          selectedTutorial: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  app.post(
    "/admin-panel/manage-category/video/delete",
    async ({ req, assert, res, api, flash }) => {
      const { body } = req;

      const { tutorialId } = body;

      assert(tutorialId, 400, "You need to provide a tutorial/video id");

      try {
        await api(`tutorial-categories-videos?tutorialId=${tutorialId}`, {
          method: "DELETE",
        });

        flash("success", `Tutorial deleted succesfully`);
      } catch (err) {
        flash("error", "Error deleting tutorial");
      }

      res.refreshWith(
        {
          "modal[type]": "",
          selectedTutorial: "",
        },
        { url: "back", hash: "#_" }
      );
    }
  );

  // Preview (apply=false) or write (apply=true) a coach program import.
  // Preview reads the chosen file; Import re-sends the previewed JSON from a
  // hidden field, so what gets written is exactly what was reviewed.
  // The result (and the JSON) goes through the session so the page can render it.
  app.post(
    "/admin-panel/import-program",
    async ({ req, res, api, flash, session }) => {
      const { body, files } = req;
      const apply = body.apply === true || body.apply === "true";
      const program = files?.file ? await files.file.text() : body.program;

      const response = await api("admin-import-program", {
        method: "POST",
        body: { coachEmail: body.coachEmail, apply, program },
      });
      const data = await response.json();

      if (!response.ok) {
        flash("error", data?.error?.message || "Import failed");
      } else {
        if (apply && !data.errors?.length)
          flash(
            "success",
            data.cycleId ? "Program imported" : "Library updated"
          );
        setSession(session, "adminImportProgram", {
          ...data,
          apply,
          // only a clean preview needs the JSON, for its Import button
          program: !apply && !data.errors?.length ? program : undefined,
        });
      }

      res.refreshWith(
        { coachEmail: body.coachEmail },
        { url: "back", hash: "#_" }
      );
    }
  );

  // Preview (apply=false) or write (apply=true) a coach meal plan import.
  // Same flow as the program import: Preview reads the chosen file, Import
  // re-sends the previewed JSON from a hidden field.
  app.post(
    "/admin-panel/import-meal-plan",
    async ({ req, res, api, flash, session }) => {
      const { body, files } = req;
      const apply = body.apply === true || body.apply === "true";
      const plan = files?.file ? await files.file.text() : body.plan;

      const response = await api("admin-import-meal-plan", {
        method: "POST",
        body: {
          coachEmail: body.coachEmail,
          title: body.title,
          description: body.description,
          apply,
          plan,
          choices: body.choices,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        flash("error", data?.error?.message || "Import failed");
      } else {
        if (apply && !data.errors?.length)
          flash("success", "Meal plan imported");
        setSession(session, "adminImportMealPlan", {
          ...data,
          apply,
          // a preview keeps the JSON for its Import and Update preview buttons
          plan: apply ? undefined : plan,
        });
      }

      res.refreshWith(
        {
          coachEmail: body.coachEmail,
          title: body.title,
          description: body.description,
        },
        { url: "back", hash: "#_" }
      );
    }
  );
};

export default Admin;
